/**
 * Restates a recipe's ingredient amounts for a different machine.
 *
 * The page is written for one full load of the baseline — ten trays, 0.6 m² — so every other
 * machine is an area ratio and every tray count is a fraction of it:
 *
 *     factor = (model area ÷ baseline area) × (trays ÷ model tray count)
 *
 * All of it happens in the browser. The amounts are already on the page and the only thing that
 * changes is a multiplier, so a round trip would buy nothing but latency.
 *
 * ## What it will not touch
 *
 * `recipe.tray_scaling.fixed` lists the lines that must not move, worked out when the recipe was
 * written and re-derived at publish time. A vessel — "1/2 bowl lemon juice", the dip that stops
 * apple slices browning — is in that list. Multiplying it would tell somebody with a 60-tray
 * machine to fetch sixteen bowls.
 *
 * **Nor the drying time.** Only the amounts change. Josh settled this on 27 Sep 2026: the time does
 * not need adjusting per model. It is governed by how thick the food is cut, not by how many trays
 * of it are in the cabinet, and every machine here is built to move enough air across a full load.
 * So the temperature and the hours on the page are right for all seven, and this leaves them alone
 * rather than deriving a correction nobody asked for.
 *
 * ## Why it re-reads the original text every time
 *
 * Each line keeps its published wording in `data-original`, and every change is applied to that
 * rather than to whatever is currently displayed. Scaling the displayed value would compound its
 * own rounding — the fault that turned "60 ripe figs" into 61 and would have gone on to 62.
 */

const BASELINE_AREA_M2 = 0.6;

/**
 * The machines, mirroring `SELECTABLE_MODELS` in src/lib/calc/models.ts.
 *
 * Duplicated because a Liquid theme cannot import TypeScript. `models.test.ts` holds this copy
 * against that one, so the two cannot drift silently.
 */
const MODELS = [
  {
    id: '10cud-a',
    name: '10 Tray',
    trays: 10,
    area: 0.6,
    image: 'https://cdn.shopify.com/s/files/1/0577/6225/0824/files/8._10CUDG_Front.png',
    trayImage:
      'https://cdn.shopify.com/s/files/1/0577/6225/0824/files/66b2bc45d35729dc864fd529_MT30_2a911885-df86-46d9-b4fb-1ec83a7ed52d.webp',
    trayLabel: '20 × 30 cm tray',
    trayLabelImperial: '8" × 12" tray',
  },
  {
    id: '16cudg',
    name: '16 Tray',
    trays: 16,
    area: 2.56,
    image: 'https://cdn.shopify.com/s/files/1/0577/6225/0824/files/8._16CUDG_Front_Close_Trays.png',
    trayImage:
      'https://cdn.shopify.com/s/files/1/0577/6225/0824/files/61b94df587eced3e0b705616_40cm_20x_2040cm_20Mesh_20Tray_832b8095-91fb-46b6-ba4c-f5bf35737417.jpg',
    trayLabel: '40 × 40 cm tray',
    trayLabelImperial: '16" × 16" tray',
  },
  {
    id: '30cud',
    name: '30 Tray',
    trays: 30,
    area: 4.8,
    image: 'https://cdn.shopify.com/s/files/1/0577/6225/0824/files/6._30CUDG_Front_Closed_Trays.png',
    trayImage:
      'https://cdn.shopify.com/s/files/1/0577/6225/0824/files/61b94df587eced3e0b705616_40cm_20x_2040cm_20Mesh_20Tray_832b8095-91fb-46b6-ba4c-f5bf35737417.jpg',
    trayLabel: '40 × 40 cm tray',
    trayLabelImperial: '16" × 16" tray',
  },
  {
    id: '32hcud',
    name: '32 Tray Horizontal',
    trays: 32,
    area: 5.12,
    image: 'https://cdn.shopify.com/s/files/1/0577/6225/0824/files/7._32HCUDG_Front_Closed.png',
    trayImage:
      'https://cdn.shopify.com/s/files/1/0577/6225/0824/files/61b94df587eced3e0b705616_40cm_20x_2040cm_20Mesh_20Tray_832b8095-91fb-46b6-ba4c-f5bf35737417.jpg',
    trayLabel: '40 × 40 cm tray',
    trayLabelImperial: '16" × 16" tray',
  },
  {
    id: '32vcud',
    name: '32 Tray Vertical',
    trays: 32,
    area: 5.12,
    image: 'https://cdn.shopify.com/s/files/1/0577/6225/0824/files/32VCUDG_Closed_Front.png',
    trayImage:
      'https://cdn.shopify.com/s/files/1/0577/6225/0824/files/61b94df587eced3e0b705616_40cm_20x_2040cm_20Mesh_20Tray_832b8095-91fb-46b6-ba4c-f5bf35737417.jpg',
    trayLabel: '40 × 40 cm tray',
    trayLabelImperial: '16" × 16" tray',
  },
  {
    id: '60cud',
    name: '60 Tray',
    trays: 60,
    area: 9.6,
    image:
      'https://cdn.shopify.com/s/files/1/0577/6225/0824/files/60CUDG_Front_Closed_Trays_260c24c7-fc09-4964-8d14-eb5ee93dbea2.png',
    trayImage:
      'https://cdn.shopify.com/s/files/1/0577/6225/0824/files/61b94df587eced3e0b705616_40cm_20x_2040cm_20Mesh_20Tray_832b8095-91fb-46b6-ba4c-f5bf35737417.jpg',
    trayLabel: '40 × 40 cm tray',
    trayLabelImperial: '16" × 16" tray',
  },
  {
    id: '28cud',
    name: '28 Tray',
    trays: 28,
    area: 11.9,
    image: 'https://cdn.shopify.com/s/files/1/0577/6225/0824/files/8._28CUDG_Closed_Fornt_Trays.png',
    trayImage:
      'https://cdn.shopify.com/s/files/1/0577/6225/0824/files/61b94df587eced9f21705804_Tray-_0000s_0005_28MeshTrays_1Stack_c6b65e43-5590-48b5-900a-2e03359d551c.png',
    trayLabel: '50 × 85 cm tray',
    trayLabelImperial: '20" × 33.5" tray',
  },
];

/**
 * Biltong hangs, so it is sized in hooks rather than trays: a layer holds `hooks × 250 g`.
 *
 * Mirrors `BILTONG_MACHINES` in src/lib/calc/models.ts, which `models.test.ts` holds this copy to.
 * The 28 tray's figure is its two-zone bundle (240 hooks) with 300 g steaks.
 */
const BILTONG_STEAK_GRAMS = 250;
const GRAMS_PER_OUNCE = 28.3495;
const CDN = 'https://cdn.shopify.com/s/files/1/0577/6225/0824/files';
const BILTONG_MACHINES = [
  { id: '10cud-a', name: '10 Tray', layers: 1, hooksPerLayer: 15, layerWord: 'layer', image: `${CDN}/10CUDG-Main_7a33b749-e297-4cc3-83ae-211a8f80ad7c.webp` },
  { id: '16cudg', name: '16 Tray', layers: 2, hooksPerLayer: 30, layerWord: 'layer', image: `${CDN}/16CUDG-BB.png` },
  { id: '30cud', name: '30 Tray', layers: 3, hooksPerLayer: 30, layerWord: 'zone', image: `${CDN}/30CUDG-BB.png` },
  { id: '32hcud', name: '32 Tray Horizontal', layers: 4, hooksPerLayer: 30, layerWord: 'layer', image: `${CDN}/32HCUDG-BB.png` },
  { id: '32vcud', name: '32 Tray Vertical', layers: 4, hooksPerLayer: 30, layerWord: 'layer', image: `${CDN}/32VCUDG-BB.png` },
  { id: '60cud', name: '60 Tray', layers: 6, hooksPerLayer: 30, layerWord: 'layer', image: `${CDN}/60CUDG-BB.png` },
  { id: '28cud', name: '28 Tray', layers: 4, hooksPerLayer: 60, steakGrams: 300, layerWord: 'layer', image: `${CDN}/28CUDG-Main.png` },
];

/**
 * Per selector photograph: [width / height, blank space under the unit as a fraction of the height].
 * Measured from the files, because the photographs are not cropped alike (the 32 Tray Horizontal
 * leaves 15% blank below it, the 30 Tray 5%). The selector uses the blank to stand every unit on
 * the same floor. A photograph missing here is simply not shifted.
 */
const IMAGE_FLOOR = {
  '8._10CUDG_Front.png': [1.3333, 0.1378],
  '8._16CUDG_Front_Close_Trays.png': [1.3333, 0.1052],
  '6._30CUDG_Front_Closed_Trays.png': [1.3333, 0.0533],
  '7._32HCUDG_Front_Closed.png': [1.3333, 0.1526],
  '32VCUDG_Closed_Front.png': [1.3333, 0.0578],
  '60CUDG_Front_Closed_Trays_260c24c7-fc09-4964-8d14-eb5ee93dbea2.png': [1.3333, 0.0652],
  '8._28CUDG_Closed_Fornt_Trays.png': [1.3333, 0.0548],
  '10CUDG-Main_7a33b749-e297-4cc3-83ae-211a8f80ad7c.webp': [1, 0.0537],
  '16CUDG-BB.png': [1.3333, 0.0652],
  '30CUDG-BB.png': [1.3333, 0.0704],
  '32HCUDG-BB.png': [1.3333, 0.0696],
  '32VCUDG-BB.png': [1.3333, 0.0681],
  '60CUDG-BB.png': [1.3333, 0.0689],
  '28CUDG-Main.png': [1.3333, 0.0506],
};

function floorStyle(url) {
  const entry = IMAGE_FLOOR[String(url).split('/').pop().split('?')[0]];
  return entry ? ` style="aspect-ratio: ${entry[0]}; --floor: ${entry[1]};"` : '';
}

/**
 * A Shopify CDN URL asked for at a given width.
 *
 * `?` or `&` depending on what the URL already carries. The first version always used `&`, which
 * turned `…/8._10CUDG_Front.png` into `…png&width=240` — not a query string at all, so every
 * photograph in the selector rendered as a broken-image icon.
 */
function sized(url, width) {
  return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
}

/**
 * The words that are a unit of measure, and nothing else.
 *
 * A closed list on purpose. Taking whatever word follows the number turned "2 garlic cloves" into
 * two *garlics*, pluralised it to "garlics", and let a count carry a fraction — "3 1/2 garlics
 * cloves". `clove` is deliberately absent: a clove is a thing you count, not a measure.
 */
const UNITS = new Set([
  'cup', 'cups', 'tablespoon', 'tablespoons', 'tbsp', 'tbs', 'teaspoon', 'teaspoons', 'tsp',
  'ml', 'millilitre', 'millilitres', 'l', 'litre', 'litres', 'liter', 'liters',
  'pint', 'pints', 'quart', 'quarts', 'gallon', 'gallons',
  'g', 'gram', 'grams', 'kg', 'kilogram', 'kilograms',
  'oz', 'ounce', 'ounces', 'lb', 'lbs', 'pound', 'pounds',
]);

const METRIC_MASS = { g: 1, gram: 1, grams: 1, kg: 1000, kilogram: 1000, kilograms: 1000 };
const IMPERIAL_MASS = { oz: 1, ounce: 1, ounces: 1, lb: 16, lbs: 16, pound: 16, pounds: 16 };

/**
 * The spoon-and-cup ladder, in teaspoons.
 *
 * A 28-tray load is twenty times a ten-tray one, and twenty times seven tablespoons is 139 of them
 * — an amount nobody can act on. Kept in teaspoons and re-expressed at the top of the ladder it is
 * eight and three quarter cups, which is a measuring jug twice.
 *
 * Only this family climbs. Millilitres and pints are already the unit their recipe chose.
 */
const SPOONS = {
  tsp: 1, teaspoon: 1, teaspoons: 1,
  tbsp: 3, tbs: 3, tablespoon: 3, tablespoons: 3,
  cup: 48, cups: 48,
};

/**
 * Where an amount changes unit: three teaspoons are a tablespoon and four tablespoons are a quarter
 * cup, so each rung starts at the smallest amount that is worth writing in it. Mirrors
 * `src/lib/recipe/measure-ladder.ts`, which writes the catalogue; `models.test.ts` holds the two
 * together, because a line that changes unit the moment the dropdown is touched looks like a bug.
 *
 * A rung is only used if it can say the amount. In cups 5 tablespoons is 5/16, which has to be written
 * 1/4 or 3/8 — a fifth out — while "5 tablespoons" is exact. So an amount stays in the smaller unit
 * unless the rounded figure is within `MEASURE_TOLERANCE` of the real one.
 */
const MEASURE_TOLERANCE = 0.07;

const roundQuarter = (value) => (value >= 10 ? Math.round(value) : Math.round(value * 4) / 4);

/** Eighths below two cups: 6 tablespoons is 3/8 of a cup, and quarters would call it a half. */
const roundCup = (value) => {
  if (value >= 10) return Math.round(value);
  return value >= 2 ? Math.round(value * 4) / 4 : Math.round(value * 8) / 8;
};

/** Largest first. `from` is in teaspoons. */
const SPOON_RUNGS = [
  { word: 'cup', teaspoons: 48, from: 12, snap: roundCup },
  { word: 'tablespoon', teaspoons: 3, from: 3, snap: roundQuarter },
];

/** The same ladder for an Australian cook, whose tablespoon is 20 ml: four teaspoons, not three. */
const AU_SPOON_RUNGS = [
  { word: 'cup', teaspoons: 48, from: 12, snap: roundCup },
  { word: 'tablespoon', teaspoons: 4, from: 4, snap: roundQuarter },
];

/** Drinks and stocks in ounces. Mirrors `POURED` in `src/lib/recipe/measure-ladder.ts`. */
const POURED =
  /\b(beer|ale|lager|stout|wine|liquor|liqueur|rum|vodka|whiske?y|bourbon|brandy|sherry|cider|juice|milk|water|broth|stock|vinegar|coffee)\b/i;

function isMeasurable(exact, snap) {
  return Math.abs(snap(exact) - exact) / exact <= MEASURE_TOLERANCE;
}

/** "2 1/2", then "3/4", then "1.5". Order matters: the plain number matches the 1 of 1/4. */
const NUMBER = /^(\s*)(\d+\s+\d+\/\d+|\d+\/\d+|\d+(?:\.\d+)?)/;

/** Two quantities joined by a dash or "to": "2 to 4", "1-2", "1 1/2 – 2". */
const RANGE =
  /^(\s*)(\d+\s+\d+\/\d+|\d+\/\d+|\d+(?:\.\d+)?)(\s*(?:-|–|to)\s*)(\d+\s+\d+\/\d+|\d+\/\d+|\d+(?:\.\d+)?)(.*)$/i;

function parseQuantity(text) {
  const mixed = text.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (mixed) return Number(mixed[1]) + Number(mixed[2]) / Number(mixed[3]);
  const fraction = text.match(/^(\d+)\/(\d+)$/);
  if (fraction) return Number(fraction[1]) / Number(fraction[2]);
  const plain = Number(text);
  return Number.isFinite(plain) ? plain : null;
}

const FRACTIONS = {
  0.125: '1/8', 0.25: '1/4', 0.375: '3/8', 0.5: '1/2', 0.625: '5/8', 0.75: '3/4', 0.875: '7/8',
};

/** A vulgar fraction, for the units a measuring jug actually has markings for. */
function asFraction(value, snap = roundQuarter) {
  const snapped = snap(value);
  const whole = Math.floor(snapped);
  const part = Math.round((snapped - whole) * 1000) / 1000;
  if (FRACTIONS[part]) return whole ? `${whole} ${FRACTIONS[part]}` : FRACTIONS[part];
  return String(snapped || 0.25);
}

/**
 * A mass in the unit that suits its size.
 *
 * A tenth of "2.5 kg" is 250 g, not "0.3 kg" — rounding a quarter-kilo to one decimal place loses
 * most of the number. So a metric mass crosses to grams below a kilo and back above one, and an
 * imperial mass crosses to ounces below a pound.
 */
function formatMass(grams, metric, keepOunces = false) {
  if (metric) {
    if (grams >= 1000) {
      const kg = Math.round(grams / 100) / 10;
      return { value: Number.isInteger(kg) ? String(kg) : kg.toFixed(1), unit: 'kg' };
    }
    const rounded = grams >= 100 ? Math.round(grams / 10) * 10 : Math.max(5, Math.round(grams / 5) * 5);
    return { value: String(rounded), unit: 'g' };
  }

  const ounces = grams;
  // Only if pounds can say it: 18 ounces is a pound and an eighth, which a quarter pound would overstate.
  if (ounces >= 16 && !keepOunces && isMeasurable(ounces / 16, roundQuarter)) {
    const lb = roundQuarter(ounces / 16);
    return { value: asFraction(lb), unit: lb > 1 ? 'pounds' : 'pound' };
  }
  const rounded = Math.max(0.25, Math.round(ounces * 4) / 4);
  return { value: asFraction(rounded), unit: rounded > 1 ? 'ounces' : 'ounce' };
}

/**
 * Abbreviations that stay as they are: "3 tsp", never "3 tsps".
 *
 * A named set rather than a length rule. The length rule was `<= 3`, which is exactly the length of
 * `cup` — so a recipe scaled to three and a half cups read "3 1/2 cup".
 */
const ABBREVIATIONS = new Set(['tsp', 'tbsp', 'tbs', 'ml', 'l', 'fl']);

/**
 * Measure words that carry the plural themselves, when the item leads with one.
 *
 * "1 recipe Flaxseed Base" scaled to nine is nine *recipes* of the same base, not nine bases.
 */
const LEADING_MEASURE =
  /^(recipes?|batch(?:es)?|portions?|servings?|quantit(?:y|ies)|drops?|droplets?|knobs?|bunch(?:es)?|pinch(?:es)?|dash(?:es)?|cloves?|heads?|sprigs?|stalks?|slices?|pieces?|strips?|leaf|leaves|sheets?|ears?|rashers?|fillets?)$/i;

/**
 * Words English does not count.
 *
 * Mirrors the set in `scripts/apply-tray-scaling.ts`, which is where "2 gingers, mashed" and
 * "essential oils" were first caught. The catalogue is written once by that script and rewritten
 * on every model change by this one, so a rule in only one of them shows up the moment a customer
 * touches the dropdown.
 */
const MASS_NOUNS = new Set([
  'ginger', 'honey', 'salt', 'sugar', 'water', 'flour', 'oil', 'vinegar', 'syrup', 'juice',
  'cinnamon', 'vanilla', 'paprika', 'cornstarch', 'cornflour', 'yeast', 'nutmeg', 'cardamom',
  'turmeric', 'parsley', 'cilantro', 'coriander', 'rosemary', 'thyme', 'oregano', 'basil',
  'powder', 'tahini',
]);

/**
 * "1 lemon" scaled to six is six lemon**s**, and "Juice of 6 limes" scaled back down is one lime.
 *
 * Only for a bare count — a line with a unit already carries its plural in the right place.
 *
 * ## Which word takes it
 *
 * The head noun, which is usually but not always the last. "1 small knob of ginger" tripled is
 * three *knobs* of ginger; taking the last word gave "3 small knob of gingers", ginger being a
 * mass noun besides. So: the word before " of " when there is one, the leading measure word when
 * the item opens with one, and otherwise the last.
 *
 * Timid about English beyond that: anything already ending in a sibilant is left alone, because
 * "squashs" reads worse than a singular that is a little short.
 */
function headNounIndex(words) {
  const of = words.findIndex((w) => w.toLowerCase() === 'of');
  if (of > 0) return of - 1;
  // Within the first two, because an adjective can precede it: "1 large bunch fresh cilantro".
  const measure = words.slice(0, 2).findIndex((w) => LEADING_MEASURE.test(w));
  if (measure !== -1) return measure;
  return words.length - 1;
}

function pluraliseItem(rest, count) {
  const match = rest.match(/^(\s*)([A-Za-z]+(?:\s+[A-Za-z]+)*)/);
  if (!match) return rest;

  const words = match[2].split(/\s+/);
  const at = headNounIndex(words);
  const word = words[at];
  const tail = rest.slice(match[0].length);

  // Back down to one: "Juice of 6 limes" at a smaller machine is the juice of one lime.
  if (count <= 1) {
    if (!/[^aeiou]ies$/i.test(word) && !/[a-z]s$/i.test(word)) return rest;
    words[at] = /[^aeiou]ies$/i.test(word) ? `${word.slice(0, -3)}y` : word.slice(0, -1);
    return `${match[1]}${words.join(' ')}${tail}`;
  }

  // A unit abbreviation, not a noun: "1 m of ribbon" scaled to eleven read "11 ms of ribbon".
  if (word.length <= 2) return rest;
  if (/s$/i.test(word)) return rest;
  if (MASS_NOUNS.has(word.toLowerCase())) return rest;

  // A sibilant takes `es`: bunches, squashes, boxes. An earlier version left them singular rather
  // than write "bunchs", which then read "3 large bunch fresh cilantro".
  words[at] = /(x|z|ch|sh)$/i.test(word)
    ? `${word}es`
    : /[^aeiou]y$/i.test(word)
      ? `${word.slice(0, -1)}ies`
      : `${word}s`;

  /*
   * The article goes with the singular. "1/2 a batch of seasoned breadcrumbs" tripled is three
   * batches, not "3 a batches" — the `a` was doing the work the number now does.
   */
  if (at > 0 && /^an?$/i.test(words[0])) words.shift();

  return `${match[1]}${words.join(' ')}${tail}`;
}

/** Pluralises a measure, never an ingredient. */
function pluralise(unit, value) {
  const bare = unit.replace(/\.$/, '');
  if (ABBREVIATIONS.has(bare.toLowerCase())) return unit;
  const singular = bare.replace(/s$/i, '');
  return value > 1 ? `${singular}s` : singular;
}

/**
 * "Juice of 3 limes" — the quantity after a leading word.
 *
 * Six lines in the catalogue put it there, and nothing that looks for a number at the start of a
 * line can see it. So the recipe scaled around them and `dehydrated-salsa` published three and a
 * half pounds of tomatoes beside the juice of three limes, unchanged.
 *
 * Handled by lifting the prefix off, scaling what is left, and putting it back — rather than
 * rewriting the line as millilitres of juice, which would be a worse instruction: you juice a lime,
 * you do not measure it.
 */
const QUANTITY_AFTER_WORD = /^(\s*(?:the\s+)?(?:juice|juices|zest|peel|rind)\s+of\s+)/i;

function rescaleLine(original, factor, au) {
  const prefix = original.match(QUANTITY_AFTER_WORD);
  if (prefix) {
    const rest = original.slice(prefix[0].length);
    const scaled = rescaleLine(rest, factor, au);
    return scaled === rest ? original : prefix[0] + scaled;
  }

  /*
   * "2 to 4 canned chipotles", "1-2 cups". Both ends move.
   *
   * Only the first number was scaled, so a 16 tray read "9 to 4 canned chipotle peppers". Each end
   * is scaled as a line of its own and the two are rejoined, because they can land on different
   * rungs of the measure ladder: at four times the load, 1 to 3 teaspoons is 4 teaspoons to 12
   * teaspoons, which is 1 1/3 tablespoons to 1/4 cup, so the unit is kept on each end when the ends
   * differ.
   */
  const range = original.match(RANGE);
  if (range) {
    const tail = range[5];
    const low = rescaleLine(range[1] + range[2] + tail, factor, au);
    const high = rescaleLine(range[1] + range[4] + tail, factor, au);
    const lowMatch = low.match(NUMBER);
    const highMatch = high.match(NUMBER);
    if (lowMatch && highMatch) {
      const lowRest = low.slice(lowMatch[0].length);
      const highRest = high.slice(highMatch[0].length);
      // Whatever the two ends share at the back is the ingredient; what is left in front of it is
      // the unit, and is written once when both ends use the same one.
      let shared = 0;
      while (
        shared < lowRest.length &&
        shared < highRest.length &&
        lowRest[lowRest.length - 1 - shared] === highRest[highRest.length - 1 - shared]
      ) {
        shared += 1;
      }
      const lowUnit = lowRest.slice(0, lowRest.length - shared);
      const highUnit = highRest.slice(0, highRest.length - shared);
      const sameUnit = lowUnit.replace(/s$/, '') === highUnit.replace(/s$/, '');
      return `${lowMatch[0]}${sameUnit ? '' : lowUnit}${range[3]}${highMatch[2]}${highRest}`;
    }
    return original;
  }

  const match = original.match(NUMBER);
  if (!match) return original;

  const value = parseQuantity(match[2]);
  if (value === null || !Number.isFinite(value) || value <= 0) return original;

  let rest = original.slice(match[0].length);

  // A unit only counts as one if it is on the list; otherwise the number is a count and the word
  // after it belongs to the ingredient.
  const after = rest.match(/^(\s*)([a-zA-Z]+\.?)/);
  const word = after ? after[2].toLowerCase().replace(/\.$/, '') : null;
  const unit = word && UNITS.has(word) ? after[2] : null;
  if (unit) rest = rest.slice(after[0].length);

  /*
   * A restatement of the quantity in another unit goes: "1 tablespoon (15 ml) soy sauce" at four
   * times the size is not "1/4 cup (15 ml) soy sauce". It is the figure a reader would trust, and
   * it would be wrong. Only straight after the quantity, and only a measure on its own.
   */
  rest = rest.replace(
    /^\s*\(\s*(?:about\s+|approx\.?\s+|approximately\s+)?[\d.,/\s]+\s*(?:ml|millilitres?|litres?|liters?|l|g|grams?|kg|kilograms?|oz|ounces?|lbs?|pounds?)\s*\)/i,
    '',
  );

  const scaled = value * factor;
  let head;

  const metricKey = unit && METRIC_MASS[unit.toLowerCase()];
  const imperialKey = unit && IMPERIAL_MASS[unit.toLowerCase()];

  if (metricKey || imperialKey) {
    const base = scaled * (metricKey || imperialKey);
    // "39 ounces dark Belgian beer" is fluid ounces; it is never "2 1/2 pounds" of beer.
    const mass = formatMass(base, Boolean(metricKey), imperialKey === 1 && POURED.test(rest));
    head = `${match[1]}${mass.value} ${mass.unit}`;
  } else if (unit && SPOONS[unit.toLowerCase().replace(/\.$/, '')]) {
    // An Australian tablespoon is 20 ml, four teaspoons, where the recipe's is 15 ml, three. The page
    // converts the lines to Australian spoons before this runs, so for that reader a tablespoon is four.
    const spoon = unit.toLowerCase().replace(/\.$/, '');
    const teaspoons = scaled * (au && SPOONS[spoon] === 3 ? 4 : SPOONS[spoon]);
    const rung = (au ? AU_SPOON_RUNGS : SPOON_RUNGS).find(
      (r) => teaspoons >= r.from && isMeasurable(teaspoons / r.teaspoons, r.snap),
    );
    const word = rung ? rung.word : 'teaspoon';
    const snap = rung ? rung.snap : roundQuarter;
    const value = teaspoons / (rung ? rung.teaspoons : 1);
    // Plural on what is written, not on what it rounded from: 17 tablespoons is "1 cup", not "1 cups".
    head = `${match[1]}${asFraction(value, snap)}${after[1]}${pluralise(word, snap(value))}`;
  } else if (unit) {
    head = `${match[1]}${asFraction(scaled)}${after[1]}${pluralise(unit, roundQuarter(scaled))}`;
  } else {
    // A count of whole things: nobody buys three and a half cloves.
    const count = Math.max(1, Math.round(scaled));
    head = `${match[1]}${count}`;
    rest = pluraliseItem(rest, count);
  }

  /*
   * The count in brackets moves with the weight in front of it.
   *
   * "2.5 kg apples (about 14 medium)" at four times the size is 10 kg and about 56 apples. Leaving
   * the bracket alone would leave the line contradicting itself, and the bracket is the number a
   * reader actually shops by.
   */
  /*
   * A quantity restated in a note, without the brackets that would catch it above.
   *
   * "1 large bunch fresh cilantro, about 60 g" is a note about one bunch. At sixteen bunches it
   * reads as though all sixteen weigh 60 g — and the gram figure is the one a reader believes,
   * because it looks like the precise one. Dropped rather than scaled, which is how the bracketed
   * form is handled and for the same reason.
   */
  rest = rest.replace(
    /,\s*(?:about|approx\.?|approximately|roughly)?\s*[\d.,/\s]+\s*(?:g|kg|grams?|kilograms?|oz|ounces?|lbs?|pounds?|ml|l|litres?|liters?)\s*$/i,
    '',
  );

  rest = rest.replace(/\s*\(about (\d+) medium\)/, (whole, count) => {
    const next = Math.round(Number(count) * factor);
    // Below two it goes entirely. Keeping the published figure beside a scaled weight is worse
    // than dropping it: "8 3/4 ounces apples (about 14 medium)" is a single apple wearing a label
    // that says fourteen.
    return next >= 2 ? ` (about ${next} medium)` : '';
  });

  return head + rest;
}

/*
 * Hands-on prep grows with the load.
 *
 * Slicing a kilo of beef and laying it on trays is a different job from slicing eleven and laying
 * them on sixteen, so the Prep tile follows the machine picker, and the Total tile moves with it by
 * the same difference. Marinating, soaking and drying do not: they take as long for a full 16 tray as
 * for a single tray, which is why the page gives a wait its own tile and the app stores drying time
 * per recipe rather than per model.
 *
 * Linear, because that is the honest simple answer: a fixed part (mixing the marinade) is
 * small beside the part that scales (slicing, laying out), and a model of the split would be a
 * guess. Anything that is not a plain duration is left exactly as written.
 */
const TIME_PAIR = /(\d+(?:\.\d+)?)\s*(hours?|hrs?|h|minutes?|mins?|m)\b/gi;

/** "1 hr 10 minutes" or "4hrs 5m" as minutes; null when there is no "number unit" pair. */
function minutesOf(text) {
  let total = 0;
  let found = false;
  for (const m of text.matchAll(TIME_PAIR)) {
    total += Number(m[1]) * (/^h/i.test(m[2]) ? 60 : 1);
    found = true;
  }
  return found ? total : null;
}

/**
 * A duration or a range of them as [low, high] minutes, or null when the text is anything else.
 * "6-8 hours" is [360, 480]; "4hrs 5m-4hrs 10m" is [245, 250]; "30 minutes, plus soaking time" and
 * "6 days + 30m" are null, so they are never rewritten into something they did not say.
 */
function timeSpan(text) {
  const t = String(text || '').replace(/\([^)]*\)/g, '').trim();
  if (!t || /day/i.test(t)) return null;
  const leftover = t
    .replace(TIME_PAIR, '')
    .replace(/[\d.\s,>~–-]|\bto\b|\babout\b|\bapprox\.?/gi, '');
  const parts = t.split(/\s*(?:-|–|\bto\b)\s*(?=\d)/i);
  if (parts.length === 1) {
    if (leftover) return null;
    const m = minutesOf(parts[0]);
    return m === null ? null : [m, m];
  }
  if (parts.length !== 2) return null;
  const high = minutesOf(parts[1]);
  if (high === null) return null;
  let low = minutesOf(parts[0]);
  if (low === null) {
    // "6-8 hours": the 6 has no unit of its own and takes the 8's.
    const bare = parts[0].trim().match(/^~?(\d+(?:\.\d+)?)$/);
    const unit = parts[1].match(/(hours?|hrs?|h|minutes?|mins?|m)\s*$/i);
    if (!bare || !unit) return null;
    low = Number(bare[1]) * (/^h/i.test(unit[1]) ? 60 : 1);
  }
  return leftover ? null : [low, high];
}

/** Whole minutes, to the nearest five once it is ten or more: a prep time is not that exact. */
/**
 * The most hands-on prep a load can grow to: four hours.
 *
 * The big machines are for people with the equipment to handle that volume — a slicer, a bench, help —
 * so prep stops growing with the load past a working half-day rather than reaching 8 hours for a
 * 60 tray. A recipe that already says more than four is never cut: it simply stops growing.
 */
const PREP_CAP_MINUTES = 240;

/** The prep at this load, in minutes: grown by the factor, rounded, and held at the cap. */
function scaledPrepMinutes(minutes, factor) {
  return Math.min(roundMinutes(minutes * factor), Math.max(PREP_CAP_MINUTES, minutes));
}

function roundMinutes(minutes) {
  return minutes < 10 ? Math.max(1, Math.round(minutes)) : Math.round(minutes / 5) * 5;
}

/** "20 minutes", "1 hr 10 minutes", "2 hours": the way the Prep tile is written. */
function durationText(minutes) {
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const h = `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  return rest ? `${hours} hr ${rest} ${rest === 1 ? 'minute' : 'minutes'}` : h;
}

/** "31hrs 15m", "7hrs", "45m": the way the Total tile is written. */
function clockText(minutes) {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest}m`;
  return rest ? `${hours}hrs ${rest}m` : `${hours}hrs`;
}

/** The prep time at this load, in the words it was written in. */
function scalePrep(text, factor) {
  const span = timeSpan(text);
  if (!span || factor === 1) return text;
  const low = scaledPrepMinutes(span[0], factor);
  const high = scaledPrepMinutes(span[1], factor);
  if (low === high) return durationText(low);
  if (low < 60 && high < 60) return `${low}-${high} minutes`;
  return `${durationText(low)}-${durationText(high)}`;
}

/**
 * The total time with the prep at this load: the original total plus however much the prep grew.
 * Text that is not a plain duration, or a prep that is not, is returned unchanged.
 */
function scaleTotal(totalText, prepText, factor) {
  const total = timeSpan(totalText);
  const prep = timeSpan(prepText);
  if (!total || !prep || factor === 1) return totalText;
  const grownLow = scaledPrepMinutes(prep[0], factor) - prep[0];
  const grownHigh = scaledPrepMinutes(prep[1], factor) - prep[1];
  const low = Math.max(0, Math.round(total[0] + grownLow));
  const high = Math.max(0, Math.round(total[1] + grownHigh));
  if (low === high) return clockText(low);
  // Ends on the half hour share one unit: "14.5-16.5hrs" is exact and fits a tile, where
  // "14hrs 30m-16hrs 30m" does not.
  if (low % 30 === 0 && high % 30 === 0) return `${low / 60}-${high / 60}hrs`;
  return `${clockText(low)}-${clockText(high)}`;
}

/**
 * The Serves line, scaled to the load: "Makes about 1 jar" on a bigger machine is three jars.
 *
 * Serving text is free-form ("Makes 2 servings", "4 to 6", "Makes 1/4 cup", "8 Serves"), so only
 * the one quantity is moved and the wording around it is left as written. A line with a second
 * quantity in it ("Makes 3/4 cup, or enough for 6 burgers") is returned untouched: scaling one
 * half would leave the sentence contradicting itself.
 */
function rescaleServing(original, factor) {
  if (!original || factor === 1) return original;
  const text = original.replace(/(\d+)\s*&\s*(\d+\/\d+)/g, '$1 $2');
  const lead = text.match(/^(\s*(?:makes|serves|yields?)?\s*(?:about|approx\.?|around|roughly)?\s*)(.*)$/i);
  const prefix = lead[1];
  const body = lead[2];
  const hasNumber = (s) => /\d/.test(s.replace(/\([^)]*\)/g, ''));
  const whole = (n) => Math.max(1, Math.round(Number(n) * factor));

  const range = body.match(/^(\d+(?:\.\d+)?)(\s*(?:-|–|to)\s*)(\d+(?:\.\d+)?)(.*)$/i);
  if (range) {
    if (hasNumber(range[4])) return original;
    const low = whole(range[1]);
    const high = whole(range[3]);
    const rest = /^\s*serves?\b/i.test(range[4]) ? range[4] : pluraliseItem(range[4], high);
    return prefix + (low === high ? String(low) : low + range[2] + high) + rest;
  }

  const first = body.match(NUMBER);
  if (!first || hasNumber(body.slice(first[0].length))) return original;

  // "8 Serves" is a count and a label, not a thing that has a plural.
  const label = body.match(/^(\d+)(\s*serves?\b.*)$/i);
  if (label) return prefix + whole(label[1]) + label[2];

  return prefix + rescaleLine(body, factor);
}

/*
 * Everything above this line is arithmetic on strings and runs anywhere; everything below needs a
 * browser. The split is deliberate — `models.test.ts` evaluates this file in Node and exercises
 * `rescaleLine` against the real shipped source, which it could not do if defining the class threw
 * on a missing `HTMLElement`.
 */
if (typeof HTMLElement !== 'undefined') {
class RecipeTrayScaler extends HTMLElement {
  /**
   * Waits for the rest of the page before looking for the ingredient list.
   *
   * The control renders *above* the list it rewrites, and the script is an ordinary blocking
   * `<script>` in the middle of the body. So the element upgrades the moment the parser reaches it,
   * which is 270 bytes before the `<ul>` exists — `querySelector` found nothing, the element
   * concluded there was no list to scale, and hid itself. It shipped, deployed and rendered, and
   * was invisible on every recipe.
   */
  connectedCallback() {
    // Moving the element between its two homes (see `place`) reconnects it. It is already built
    // by then and its listeners are on the inner nodes, which travel with it.
    if (this.started) return;
    this.started = true;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
    } else {
      this.init();
    }
  }

  init() {
    try {
      this.scaling = JSON.parse(this.dataset.scaling || 'null');
    } catch {
      this.scaling = null;
    }
    // No scaling data means an assembly recipe — a spice blend puts nothing on a tray — so the
    // control has no meaning and removes itself rather than offering a choice that does nothing.
    this.hung = Boolean(this.scaling && this.scaling.hung && this.scaling.baseGrams);
    if (!this.scaling || !(this.scaling.areaM2 || this.hung)) {
      this.hidden = true;
      return;
    }

    this.list = document.querySelector(this.dataset.target || '.ingredients ul');
    if (!this.list) {
      // Loud, because the silent version of this shipped: the element hid itself on every recipe
      // and the deploy, the checksums and the tests were all green.
      console.warn('[recipe-tray-scaler] no ingredient list at', this.dataset.target);
      this.hidden = true;
      return;
    }

    this.items = [...this.list.querySelectorAll('li')];
    this.items.forEach((li) => {
      if (!li.dataset.original) li.dataset.original = li.textContent;
    });

    /*
     * The same region the ingredient list itself was chosen for.
     *
     * A US reader is already being shown pounds and inches, and the trays are sold there as
     * 8" × 12" and 20" × 33.5". Naming them in centimetres beside an imperial ingredient list would
     * be the capsicum fault again — correct data, wrong country.
     */
    this.imperial = this.dataset.units === 'imperial';
    this.au = this.dataset.region === 'AU';

    this.machines = this.hung ? BILTONG_MACHINES : MODELS;
    this.model = this.machines[0];
    this.trays = 'all';
    this.place();
    this.render();
  }

  /**
   * Inside the ingredients column on a phone, a card of its own above the recipe on a desktop.
   *
   * The Liquid renders the control inside the ingredients column, which is the right place when
   * the page is one column. At the width where ingredients and instructions sit side by side that
   * column was a tall narrow box, so from 800 px — the same breakpoint the two columns use — it
   * moves out to sit between the facts strip and the recipe card, full width, where its own
   * container query lays it out as a long strip.
   *
   * Moved rather than rendered twice: two copies would each need to know what the other had
   * chosen, and the amounts on the page can only follow one of them.
   */
  place() {
    const details = this.closest('.recipe-details');
    if (!details) return;
    // Remembers the phone position, so the move can be undone when a window is narrowed.
    const home = document.createComment('recipe-tray-scaler');
    this.before(home);
    const desktop = window.matchMedia('(min-width: 800px)');
    const move = () => {
      if (desktop.matches) details.before(this);
      else home.after(this);
    };
    move();
    desktop.addEventListener('change', move);
  }

  /** Grams of meat on the hooks in use. Biltong only. */
  get hungGrams() {
    const layers = this.trays === 'all' ? this.model.layers : this.trays;
    return layers * this.model.hooksPerLayer * (this.model.steakGrams || BILTONG_STEAK_GRAMS);
  }

  get factor() {
    if (this.hung) return this.hungGrams / this.scaling.baseGrams;
    const area = this.model.area / this.scaling.areaM2;
    const share = this.trays === 'all' ? 1 : this.trays / this.model.trays;
    return area * share;
  }

  apply() {
    const factor = this.factor;
    const fixed = new Set(this.scaling.fixed || []);

    this.items.forEach((li, index) => {
      const original = li.dataset.original || '';
      if (fixed.has(index) || factor === 1) {
        li.textContent = original;
        return;
      }
      li.textContent = rescaleLine(original, factor, this.au);
    });
    // Rewriting a line's text drops the span the page put round its quantity.
    if (typeof window.uxfDecorateIngredients === 'function') window.uxfDecorateIngredients(this.list);

    const serves = document.querySelector('.recipe-info [data-serves]');
    if (serves) {
      if (serves.dataset.original === undefined) serves.dataset.original = serves.textContent.trim();
      serves.textContent = rescaleServing(serves.dataset.original, factor);
    }

    // Prep is the hands-on part and grows with the load; Total moves by the same amount.
    const tiles = {};
    document.querySelectorAll('.recipe-info li').forEach((li) => {
      const title = li.querySelector('.meta-title');
      const value = li.querySelector('.meta-text');
      if (!title || !value) return;
      const key = title.textContent.trim().toLowerCase();
      if (key === 'prep time') tiles.prep = value;
      if (key === 'total time') tiles.total = value;
    });
    for (const tile of [tiles.prep, tiles.total]) {
      if (tile && tile.dataset.original === undefined) tile.dataset.original = tile.textContent.trim();
    }
    if (tiles.prep) {
      tiles.prep.textContent = scalePrep(tiles.prep.dataset.original, factor);
      if (tiles.total) tiles.total.textContent = scaleTotal(tiles.total.dataset.original, tiles.prep.dataset.original, factor);
    }

    const note = this.querySelector('.uxf-tray__note');
    if (note && this.hung) {
      const layers = this.trays === 'all' ? this.model.layers : this.trays;
      const steak = this.model.steakGrams || BILTONG_STEAK_GRAMS;
      const mass = formatMass(this.imperial ? this.hungGrams / GRAMS_PER_OUNCE : this.hungGrams, !this.imperial);
      const steakText = this.imperial
        ? (() => { const s = formatMass(steak / GRAMS_PER_OUNCE, false, true); return `about ${s.value} ${s.unit}`; })()
        : `${steak} g`;
      note.textContent = `${layers * this.model.hooksPerLayer} hooks of ${steakText} steaks, ${mass.value} ${mass.unit} of meat in the ${this.model.name}.`;
    } else if (note) {
      note.textContent =
        factor === 1
          ? 'Amounts fill all ten trays of the 10 Tray.'
          : `Amounts scaled ${factor < 1 ? 'down' : 'up'} for ${
              this.trays === 'all' ? 'a full' : `a ${this.trays}-tray`
            } load of the ${this.model.name}.`;
    }
  }

  render() {
    this.innerHTML = `
      <div class="uxf-tray">
        <div class="uxf-tray__field">
          <span class="uxf-tray__label">Your machine</span>
          <div class="uxf-tray__machines" role="radiogroup" aria-label="Your machine"></div>
        </div>
        <div class="uxf-tray__field uxf-tray__field--count">
          <span class="uxf-tray__label">${this.hung ? 'Layers to fill' : 'Trays to fill'}</span>
          <div class="uxf-tray__count">
            <span class="uxf-tray__tray" ${this.hung ? 'hidden' : ''}>
              <img class="uxf-tray__tray-image" alt="" width="64" height="48" loading="lazy">
              <span class="uxf-tray__tray-label"></span>
            </span>
            <select class="uxf-tray__select" aria-label="${this.hung ? 'Layers to fill' : 'Trays to fill'}"></select>
          </div>
        </div>
        <p class="uxf-tray__note"></p>
      </div>`;

    const machines = this.querySelector('.uxf-tray__machines');
    machines.innerHTML = this.machines.map(
      (m) => `
      <button type="button" class="uxf-tray__machine" role="radio" data-id="${m.id}"
              aria-checked="${m.id === this.model.id}">
        <span class="uxf-tray__machine-name">${m.name}</span>
        <img src="${sized(m.image, 240)}" alt="${m.name} dehydrator" loading="lazy" width="240" height="180"${floorStyle(m.image)}>
      </button>`,
    ).join('');

    machines.addEventListener('click', (event) => {
      const button = event.target.closest('.uxf-tray__machine');
      if (!button) return;
      this.model = this.machines.find((m) => m.id === button.dataset.id) || this.model;
      this.trays = 'all';
      for (const other of machines.querySelectorAll('.uxf-tray__machine')) {
        other.setAttribute('aria-checked', String(other === button));
      }
      this.refreshTrays();
      this.apply();
    });

    this.select = this.querySelector('.uxf-tray__select');
    this.select.addEventListener('change', () => {
      this.trays = this.select.value === 'all' ? 'all' : Number(this.select.value);
      this.apply();
    });

    this.refreshTrays();
    this.apply();
  }

  /**
   * The tray list for the machine in front of you.
   *
   * Stops one short of the full count, because filling every tray **is** "All Trays" and two labels
   * for one quantity is a question nobody should have to answer.
   */
  refreshTrays() {
    if (this.hung) {
      const word = this.model.layerWord;
      const options = [`<option value="all">All ${word}s</option>`];
      for (let n = 1; n < this.model.layers; n += 1) {
        options.push(`<option value="${n}">${n} ${n === 1 ? word : word + 's'}</option>`);
      }
      this.select.innerHTML = options.join('');
      this.select.value = 'all';
      // The 10 tray is one rack and has nothing to choose between.
      this.querySelector('.uxf-tray__field--count').hidden = this.model.layers === 1;
      this.querySelector('.uxf-tray__field--count .uxf-tray__label').textContent = `${word[0].toUpperCase()}${word.slice(1)}s to fill`;
      return;
    }
    const options = ['<option value="all">All Trays</option>'];
    for (let n = 1; n < this.model.trays; n += 1) {
      options.push(`<option value="${n}">${n} ${n === 1 ? 'Tray' : 'Trays'}</option>`);
    }
    this.select.innerHTML = options.join('');
    this.select.value = 'all';

    const label = this.imperial ? this.model.trayLabelImperial : this.model.trayLabel;
    const image = this.querySelector('.uxf-tray__tray-image');
    image.src = sized(this.model.trayImage, 128);
    image.alt = label;

    const caption = this.querySelector('.uxf-tray__tray-label');
    if (caption) caption.textContent = label;
  }
}

if (!customElements.get('recipe-tray-scaler')) {
  customElements.define('recipe-tray-scaler', RecipeTrayScaler);
}
}
